import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Check, Clock, Copy, KeyRound, Send, Trash2, UserPlus, X } from "lucide-react";

/* ----------------------- Shared ----------------------- */

export function AddUserModal({ onClose, onCreated }) {
    const [form, setForm] = useState({
        name: "",
        email: "",
        password: "",
        relationship: "",
        biggest_challenge: "",
        admin_notes: "",
        auto_approve: true,
    });
    const [submitting, setSubmitting] = useState(false);
    const [created, setCreated] = useState(null);

    const suggestPassword = () => {
        // Friendly, easy-to-read temporary password the admin can text to the user.
        const words = ["Lane", "Memory", "Cream", "Sage", "Mom", "Walk", "Calm", "Story"];
        const w = words[Math.floor(Math.random() * words.length)];
        const n = Math.floor(1000 + Math.random() * 9000);
        setForm(f => ({ ...f, password: `${w}-${n}!ML` }));
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim()) {
            toast.error("Name and email are required."); return;
        }
        if (form.password.length < 8) {
            toast.error("Password must be at least 8 characters."); return;
        }
        setSubmitting(true);
        try {
            const r = await api.post("/admin/users/create", form);
            setCreated({ ...r.data.user, password: form.password });
            toast.success(form.auto_approve ? "User created and approved." : "User created on the waitlist.");
        } catch (err) {
            const detail = err?.response?.data?.detail || "Couldn't create. Try again.";
            toast.error(typeof detail === "string" ? detail : "Couldn't create. Try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const copyCreds = () => {
        if (!created) return;
        const txt =
            `Welcome to Memory Lane Care Navigator 💚\n\n` +
            `You're all set — here's how to sign in:\n\n` +
            `🔗 https://memorylanecare.net/login\n` +
            `📧 Email: ${created.email}\n` +
            `🔐 Temporary password: ${created.password}\n\n` +
            `When you sign in, head to the Profile section to update your password.\n\n` +
            `If anything looks off, just reply to this message — we're here.\n— The Memory Lane team`;
        navigator.clipboard.writeText(txt).then(
            () => toast.success("Welcome message copied — paste it into a text or email."),
            () => toast.error("Couldn't copy.")
        );
    };

    return (
        <div
            className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50"
            data-testid="admin-add-user-modal"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6 md:p-7">
                {!created && (
                    <>
                        <div className="flex items-start justify-between gap-3 mb-4">
                            <div>
                                <h2 className="text-xl md:text-2xl font-heading font-semibold tracking-tight">Add a user manually</h2>
                                <p className="mt-1 text-sm text-[#4C5551]">
                                    Use this when someone can&apos;t sign up themselves — they emailed you, your signup form was down, etc.
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={onClose}
                                aria-label="Close"
                                data-testid="admin-add-user-close"
                                className="shrink-0 w-8 h-8 rounded-full hover:bg-cream-dark flex items-center justify-center"
                            >
                                <X size={16} strokeWidth={1.8} />
                            </button>
                        </div>

                        <form onSubmit={submit} className="space-y-4">
                            <div>
                                <label className="ml-label" htmlFor="add-name">Full name *</label>
                                <input
                                    id="add-name"
                                    data-testid="admin-add-name"
                                    className="ml-input"
                                    value={form.name}
                                    onChange={e => setForm({ ...form, name: e.target.value })}
                                    placeholder="Stephanie Gabrisch"
                                    autoFocus
                                />
                            </div>
                            <div>
                                <label className="ml-label" htmlFor="add-email">Email *</label>
                                <input
                                    id="add-email"
                                    type="email"
                                    data-testid="admin-add-email"
                                    className="ml-input"
                                    value={form.email}
                                    onChange={e => setForm({ ...form, email: e.target.value })}
                                    placeholder="them@example.com"
                                />
                            </div>
                            <div>
                                <div className="flex items-end justify-between">
                                    <label className="ml-label" htmlFor="add-password">Temporary password *</label>
                                    <button
                                        type="button"
                                        onClick={suggestPassword}
                                        data-testid="admin-add-suggest-password"
                                        className="text-xs font-medium text-teal-dark hover:underline mb-1"
                                    >
                                        Suggest one
                                    </button>
                                </div>
                                <input
                                    id="add-password"
                                    data-testid="admin-add-password"
                                    className="ml-input font-mono"
                                    value={form.password}
                                    onChange={e => setForm({ ...form, password: e.target.value })}
                                    placeholder="8+ characters they can change later"
                                />
                            </div>
                            <div>
                                <label className="ml-label" htmlFor="add-rel">Relationship</label>
                                <select
                                    id="add-rel"
                                    data-testid="admin-add-relationship"
                                    className="ml-input"
                                    value={form.relationship}
                                    onChange={e => setForm({ ...form, relationship: e.target.value })}
                                >
                                    <option value="">Not specified</option>
                                    <option>Spouse</option>
                                    <option>Adult Child</option>
                                    <option>Friend</option>
                                    <option>Caregiver</option>
                                    <option>Healthcare Professional</option>
                                </select>
                            </div>
                            <div>
                                <label className="ml-label" htmlFor="add-challenge">Biggest challenge (optional)</label>
                                <textarea
                                    id="add-challenge"
                                    data-testid="admin-add-challenge"
                                    className="ml-input min-h-[70px] py-2"
                                    value={form.biggest_challenge}
                                    onChange={e => setForm({ ...form, biggest_challenge: e.target.value })}
                                    placeholder="What they told you they need help with."
                                />
                            </div>
                            <div>
                                <label className="ml-label" htmlFor="add-notes">Admin notes (private)</label>
                                <textarea
                                    id="add-notes"
                                    data-testid="admin-add-notes"
                                    className="ml-input min-h-[60px] py-2"
                                    value={form.admin_notes}
                                    onChange={e => setForm({ ...form, admin_notes: e.target.value })}
                                    placeholder="Why you created this account manually (e.g. signup form down 2026-06-10)."
                                />
                            </div>

                            <label className="flex items-center gap-2.5 cursor-pointer" data-testid="admin-add-approve-toggle">
                                <input
                                    type="checkbox"
                                    checked={form.auto_approve}
                                    onChange={e => setForm({ ...form, auto_approve: e.target.checked })}
                                    className="w-4 h-4 accent-teal-dark"
                                />
                                <span className="text-sm text-[#1F2624]">
                                    <strong>Auto-approve.</strong> They can sign in immediately. (Untick to put them on the waitlist instead.)
                                </span>
                            </label>

                            <div className="flex flex-wrap gap-2.5 justify-end pt-2">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="px-4 py-2 rounded-2xl text-sm font-medium text-[#4C5551] hover:bg-cream-dark"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    data-testid="admin-add-submit"
                                    className="inline-flex items-center gap-2 bg-teal text-white hover:bg-teal-dark disabled:opacity-60 rounded-2xl px-4 py-2 text-sm font-medium"
                                >
                                    <UserPlus size={14} strokeWidth={2} />
                                    {submitting ? "Creating…" : "Create user"}
                                </button>
                            </div>
                        </form>
                    </>
                )}

                {created && (
                    <div data-testid="admin-add-user-success">
                        <div className="text-center mb-4">
                            <div className="w-12 h-12 mx-auto rounded-2xl bg-teal-soft flex items-center justify-center mb-3">
                                <Check strokeWidth={2} className="text-teal-dark" />
                            </div>
                            <h2 className="text-xl md:text-2xl font-heading font-semibold tracking-tight">
                                {created.status === "approved" ? "Account ready" : "Added to waitlist"}
                            </h2>
                            <p className="mt-1 text-sm text-[#4C5551]">
                                {created.status === "approved"
                                    ? `Send ${created.name.split(" ")[0] || created.name} these credentials so they can sign in.`
                                    : `${created.name} is on the waitlist. Approve them when ready.`}
                            </p>
                        </div>

                        {created.status === "approved" && (
                            <div className="ml-card p-4 bg-cream-dark/60 !border-cream-dark space-y-2 font-mono text-sm">
                                <p><span className="text-[#4C5551]">Email:</span> <strong>{created.email}</strong></p>
                                <p><span className="text-[#4C5551]">Password:</span> <strong>{created.password}</strong></p>
                                <p><span className="text-[#4C5551]">Sign-in:</span> https://memorylanecare.net/login</p>
                            </div>
                        )}

                        <div className="flex flex-wrap gap-2.5 justify-end mt-5">
                            {created.status === "approved" && (
                                <button
                                    type="button"
                                    onClick={copyCreds}
                                    data-testid="admin-add-copy-welcome"
                                    className="inline-flex items-center gap-2 bg-white border border-teal text-teal-dark hover:bg-teal-soft rounded-2xl px-4 py-2 text-sm font-medium"
                                >
                                    <Copy size={14} strokeWidth={1.8} /> Copy welcome message
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={onCreated}
                                data-testid="admin-add-done"
                                className="inline-flex items-center gap-2 bg-teal text-white hover:bg-teal-dark rounded-2xl px-4 py-2 text-sm font-medium"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export function ResetPasswordModal({ user, onClose }) {
    const [confirming, setConfirming] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null); // {new_password}

    const doReset = async () => {
        setSubmitting(true);
        try {
            const r = await api.post(`/admin/users/${user.id}/reset-password`, {});
            setResult({ new_password: r.data.new_password });
            setConfirming(false);
            toast.success("Password reset.");
        } catch (err) {
            const detail = err?.response?.data?.detail || "Couldn't reset. Try again.";
            toast.error(typeof detail === "string" ? detail : "Couldn't reset.");
        } finally {
            setSubmitting(false);
        }
    };

    const copyResetMessage = () => {
        if (!result) return;
        const firstName = (user.name || "").split(" ")[0] || "there";
        const txt =
            `Hi ${firstName},\n\n` +
            `Your Memory Lane password has been reset. Here's how to sign back in:\n\n` +
            `🔗 https://memorylanecare.net/login\n` +
            `📧 Email: ${user.email}\n` +
            `🔐 New temporary password: ${result.new_password}\n\n` +
            `Sign in, then head to Profile to set a password you'll remember.\n\n` +
            `If this wasn't you or you didn't ask for a reset, reply right away.\n— The Memory Lane team`;
        navigator.clipboard.writeText(txt).then(
            () => toast.success("Reset message copied — paste it into a text or email."),
            () => toast.error("Couldn't copy.")
        );
    };

    return (
        <div
            className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50"
            data-testid="admin-reset-password-modal"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6 md:p-7">
                <div className="flex items-start justify-between gap-3 mb-4">
                    <div className="flex items-start gap-3">
                        <div className="shrink-0 w-10 h-10 rounded-2xl bg-yellow-soft flex items-center justify-center">
                            <KeyRound strokeWidth={1.7} className="text-yellow-dark" />
                        </div>
                        <div>
                            <h2 className="text-xl font-heading font-semibold tracking-tight">Reset password</h2>
                            <p className="mt-0.5 text-sm text-[#4C5551]">
                                {confirming
                                    ? `Generates a new temporary password for ${user.name || user.email}. Their current password will stop working immediately.`
                                    : `Send this to ${user.name || user.email} so they can sign back in.`}
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close"
                        data-testid="admin-reset-close"
                        className="shrink-0 w-8 h-8 rounded-full hover:bg-cream-dark flex items-center justify-center"
                    >
                        <X size={16} strokeWidth={1.8} />
                    </button>
                </div>

                {confirming && (
                    <>
                        <div className="ml-card p-4 bg-cream-dark/50 !border-cream-dark text-sm space-y-1 mb-5">
                            <p><span className="text-[#4C5551]">Name:</span> <strong>{user.name || "(no name)"}</strong></p>
                            <p><span className="text-[#4C5551]">Email:</span> <strong>{user.email}</strong></p>
                        </div>
                        <p className="text-xs text-[#6E7A74] mb-4">
                            Memory Lane will generate a friendly temporary password (e.g. <code className="px-1 rounded bg-cream-dark">Sage-4831!ML</code>).
                            You&apos;ll copy a ready-to-send message on the next screen — the new password is only shown once.
                        </p>
                        <div className="flex flex-wrap gap-2.5 justify-end">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-2 rounded-2xl text-sm font-medium text-[#4C5551] hover:bg-cream-dark"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={doReset}
                                disabled={submitting}
                                data-testid="admin-reset-confirm"
                                className="inline-flex items-center gap-2 bg-yellow-dark text-white hover:opacity-90 disabled:opacity-60 rounded-2xl px-4 py-2 text-sm font-medium"
                            >
                                <KeyRound size={14} strokeWidth={2} />
                                {submitting ? "Resetting…" : "Reset password now"}
                            </button>
                        </div>
                    </>
                )}

                {!confirming && result && (
                    <div data-testid="admin-reset-success">
                        <div className="ml-card p-4 bg-cream-dark/60 !border-cream-dark space-y-2 font-mono text-sm">
                            <p><span className="text-[#4C5551]">Email:</span> <strong>{user.email}</strong></p>
                            <p><span className="text-[#4C5551]">New password:</span> <strong>{result.new_password}</strong></p>
                            <p><span className="text-[#4C5551]">Sign-in:</span> https://memorylanecare.net/login</p>
                        </div>
                        <p className="mt-3 text-xs text-[#6E7A74]">
                            Save this somewhere — it&apos;s only shown once. The user&apos;s old password no longer works.
                        </p>
                        <div className="flex flex-wrap gap-2.5 justify-end mt-5">
                            <button
                                type="button"
                                onClick={copyResetMessage}
                                data-testid="admin-reset-copy"
                                className="inline-flex items-center gap-2 bg-white border border-teal text-teal-dark hover:bg-teal-soft rounded-2xl px-4 py-2 text-sm font-medium"
                            >
                                <Copy size={14} strokeWidth={1.8} /> Copy reset message
                            </button>
                            <button
                                type="button"
                                onClick={onClose}
                                data-testid="admin-reset-done"
                                className="inline-flex items-center gap-2 bg-teal text-white hover:bg-teal-dark rounded-2xl px-4 py-2 text-sm font-medium"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export function DeleteUserModal({ user, onClose, onDeleted }) {
    const [typed, setTyped] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const confirmText = "DELETE";
    const matches = typed.trim() === confirmText;

    const doDelete = async () => {
        if (!matches || submitting) return;
        setSubmitting(true);
        try {
            const r = await api.delete(`/admin/applications/${user.id}`);
            const counts = r.data?.deleted || {};
            const parts = Object.entries(counts)
                .filter(([k]) => k !== "users")
                .map(([k, v]) => `${k.replace(/_/g, " ")}: ${v}`);
            toast.success(
                parts.length
                    ? `Deleted ${user.email}. (${parts.join(", ")})`
                    : `Deleted ${user.email}.`
            );
            onDeleted();
        } catch (err) {
            const detail = err?.response?.data?.detail || "Couldn't delete. Try again.";
            toast.error(typeof detail === "string" ? detail : "Couldn't delete.");
            setSubmitting(false);
        }
    };

    return (
        <div
            className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50"
            data-testid="admin-delete-modal"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6 md:p-7">
                <div className="flex items-start justify-between gap-3 mb-4">
                    <div className="flex items-start gap-3">
                        <div className="shrink-0 w-10 h-10 rounded-2xl bg-emergency-light flex items-center justify-center">
                            <Trash2 strokeWidth={1.7} className="text-emergency-dark" />
                        </div>
                        <div>
                            <h2 className="text-xl font-heading font-semibold tracking-tight">Delete user</h2>
                            <p className="mt-0.5 text-sm text-[#4C5551]">
                                This permanently removes <strong>{user.name || user.email}</strong> and every
                                record tied to their session — intake, behavior log, AI chat, patient profile,
                                burnout history, appointments. Feedback rows stay (anonymized) so the Caregiver
                                pulse rollup doesn&apos;t lose data.
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close"
                        data-testid="admin-delete-close"
                        className="shrink-0 w-8 h-8 rounded-full hover:bg-cream-dark flex items-center justify-center"
                    >
                        <X size={16} strokeWidth={1.8} />
                    </button>
                </div>

                <div className="ml-card p-4 bg-cream-dark/50 !border-cream-dark text-sm space-y-1 mb-5">
                    <p><span className="text-[#4C5551]">Name:</span> <strong>{user.name || "(no name)"}</strong></p>
                    <p><span className="text-[#4C5551]">Email:</span> <strong>{user.email}</strong></p>
                    <p><span className="text-[#4C5551]">Status:</span> <strong>{user.status}</strong></p>
                </div>

                <label className="block text-xs uppercase tracking-wider font-semibold text-[#4C5551] mb-1.5">
                    Type <code className="px-1 rounded bg-cream-dark text-emergency-dark">{confirmText}</code> to confirm
                </label>
                <input
                    type="text"
                    value={typed}
                    onChange={(e) => setTyped(e.target.value)}
                    autoFocus
                    autoComplete="off"
                    data-testid="admin-delete-confirm-input"
                    className="ml-input !min-h-[44px] !py-2 text-sm font-mono"
                />

                <div className="flex flex-wrap gap-2.5 justify-end mt-5">
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="admin-delete-cancel"
                        className="px-4 py-2 rounded-2xl text-sm font-medium text-[#4C5551] hover:bg-cream-dark"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={doDelete}
                        disabled={!matches || submitting}
                        data-testid="admin-delete-confirm"
                        className="inline-flex items-center gap-1.5 bg-emergency text-white hover:bg-emergency-dark disabled:opacity-40 disabled:cursor-not-allowed rounded-2xl px-4 py-2 text-sm font-medium"
                    >
                        <Trash2 size={14} strokeWidth={2} />
                        {submitting ? "Deleting…" : "Delete forever"}
                    </button>
                </div>
            </div>
        </div>
    );
}



export function LoginStatsPill({ user }) {
    const count = user.login_count || 0;
    const last = user.last_login_at;
    // `now` is sampled once on mount and held in state so the component is pure
    // with respect to its props (the lint rule complains otherwise).
    const [now] = useState(() => Date.now());

    if (count === 0) {
        return (
            <span
                data-testid={`logins-never-${user.id}`}
                title="Approved but hasn't signed in yet."
                className="text-xs px-2.5 py-0.5 rounded-2xl bg-cream-dark text-[#6E7A74] font-medium italic"
            >
                Never logged in
            </span>
        );
    }

    const { when, tone } = formatLoginRecency(last, now);

    return (
        <span
            data-testid={`logins-stats-${user.id}`}
            title={last
                ? `Logged in ${count}× · last on ${new Date(last).toLocaleString()}`
                : `Logged in ${count} times`}
            className={`text-xs px-2.5 py-0.5 rounded-2xl font-medium ${tone}`}
        >
            {count}× · last {when}
        </span>
    );
}

export function formatLoginRecency(lastAt, now) {
    if (!lastAt) return { when: "", tone: "bg-cream-dark text-[#4C5551]" };
    const days = Math.floor((now - new Date(lastAt).getTime()) / 86400000);
    let when;
    if (days === 0) when = "today";
    else if (days === 1) when = "yesterday";
    else if (days < 7) when = `${days}d ago`;
    else if (days < 30) when = `${Math.floor(days / 7)}w ago`;
    else when = `${Math.floor(days / 30)}mo ago`;
    const tone = days <= 3
        ? "bg-stage_early/60 text-stage_early-dark"
        : days <= 14
            ? "bg-yellow-soft text-yellow-dark"
            : "bg-cream-dark text-[#4C5551]";
    return { when, tone };
}


// Format active-time seconds (server-side cumulative) as short human text:
// "12s" / "3m" / "47m" / "2h 14m". Used on per-tester rows + admin views.
export function formatActiveTime(secs) {
    const s = Math.max(0, Math.round(Number(secs) || 0));
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m`;
    const h = Math.floor(m / 60);
    return `${h}h ${m % 60}m`;
}


export function SessionStatusPill({ user }) {
    // Three states an approved user can be in for the session-id binding system:
    //  1. Synced (post-migration): has session_bound_at — was a legacy user, has logged in once since the new code shipped.
    //  2. Synced (born-new): has primary_session_id but no session_bound_at — was created under the new system at register.
    //  3. Not yet migrated: no primary_session_id — predates the new system and hasn't logged in since redeploy.
    const hasPrimary = !!user.primary_session_id;
    const wasMigrated = !!user.session_bound_at;
    if (!hasPrimary) {
        return (
            <span
                data-testid={`session-status-pending-${user.id}`}
                title="No session_id is bound yet. The user's intake/Lane data is still tied to whatever browser they last used. They'll get auto-migrated on next login."
                className="text-xs px-2.5 py-0.5 rounded-2xl bg-yellow-soft text-yellow-dark font-medium inline-flex items-center gap-1"
            >
                <Clock size={10} strokeWidth={2.2} />
                Session: not yet synced
            </span>
        );
    }
    return (
        <span
            data-testid={`session-status-synced-${user.id}`}
            title={
                wasMigrated
                    ? `Session bound on ${new Date(user.session_bound_at).toLocaleDateString()} — legacy data is now portable across devices.`
                    : "Account created with stable session_id — data follows the user automatically."
            }
            className="text-xs px-2.5 py-0.5 rounded-2xl bg-stage_early/60 text-stage_early-dark font-medium inline-flex items-center gap-1"
        >
            <Check size={10} strokeWidth={2.2} />
            Session: synced{wasMigrated ? "" : " (new)"}
        </span>
    );
}

export function StatCard({ label, value, tone }) {
    const styles = {
        yellow: "bg-yellow-soft text-yellow-dark",
        teal: "bg-teal-soft text-teal-dark",
        coral: "bg-coral-light text-coral-dark",
        lavender: "bg-stage_late-light text-stage_late-dark",
    }[tone];
    return (
        <div className={`ml-card p-5 ${styles} !border-transparent`}>
            <p className="text-xs uppercase tracking-wider font-semibold">{label}</p>
            <p className="text-3xl font-heading font-semibold mt-1">{value}</p>
        </div>
    );
}


export function timeAgo(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.round(diff / 60)} min ago`;
    if (diff < 86400) return `${Math.round(diff / 3600)} hr ago`;
    return `${Math.round(diff / 86400)} day(s) ago`;
}

export function shortUA(ua) {
    if (!ua) return "—";
    if (/iPhone|iPad/i.test(ua)) return "iOS";
    if (/Android/i.test(ua)) return "Android";
    if (/Macintosh/i.test(ua)) return "macOS";
    if (/Windows/i.test(ua)) return "Windows";
    if (/Linux/i.test(ua)) return "Linux";
    return ua.split(" ")[0];
}

